import { z } from 'zod';
import { api } from '../../../general/apiScenarios';
import { CHNode } from '../../../general/node';

/**
 * Тонкий адаптер над `api.crhub.googleDriveImport` из общего API-слоя.
 * Zod-схемы серверных payload-ов живут только здесь; URL и имена серверных полей —
 * только в `src/general/apiScenarios.ts`.
 */

const createTaskResponseSchema = z.object({
  task_id: z.string(),
  direct_uploading_id: z.number(),
});

const progressSchema = z.object({ progress: z.number() });

/**
 * SUCCESS-результат задачи импорта: плоский массив id созданных нод (не объект с `node_ids`).
 * В нём только узлы верхнего уровня — созданные непосредственно в целевой папке;
 * содержимое импортированной папки в массив не входит.
 */
const importedNodeIdsSchema = z.array(z.number());

const stringListSchema = z.array(z.string()).min(1);

export type CreateImportTaskResult =
  | { ok: true; taskId: string; directUploadingId: number }
  | { ok: false; error: string };

/**
 * Разбор текста ошибки: FAILURE-результат задачи и синхронные ответы POST-а.
 * Покрывает только реальные форматы бэкенда, первое совпадение — результат.
 */
export function parseErrorMessage(data: unknown, fallback: string): string {
  // FAILURE из celery-таски: { message: string } (готовый текст DirectUploadingException)
  const message = z
    .object({ message: z.union([z.string(), z.number(), z.boolean()]) })
    .safeParse(data);
  if (message.success) {
    return String(message.data.message);
  }

  // 401/403 от DRF
  const detail = z.object({ detail: z.string() }).safeParse(data);
  if (detail.success) {
    return detail.data.detail;
  }

  // ошибки сериализатора по полям
  const link = z.object({ link: stringListSchema }).safeParse(data);
  if (link.success) {
    return link.data.link.join('\n');
  }

  const parentNodeId = z.object({ parent_node_id: stringListSchema }).safeParse(data);
  if (parentNodeId.success) {
    return parentNodeId.data.parent_node_id.join('\n');
  }

  // ValidationError из вью
  const plainList = stringListSchema.safeParse(data);
  if (plainList.success) {
    return plainList.data.join('\n');
  }

  // общий формат проекта
  const nested = z.object({ data: stringListSchema }).safeParse(data);
  if (nested.success) {
    return nested.data.data.join('\n');
  }

  return fallback;
}

/** Создание задачи импорта. */
export async function createImportTask(params: {
  link: string;
  parentNodeId: CHNode['id'];
}): Promise<CreateImportTaskResult> {
  const result = await api.crhub.googleDriveImport.uploadFromLink({
    link: params.link,
    parent_node_id: params.parentNodeId,
  });

  if (!result.success) {
    return {
      ok: false,
      error: parseErrorMessage(result.data.response, 'Failed to start import'),
    };
  }

  const parsed = createTaskResponseSchema.safeParse(result.data.response);

  if (!parsed.success) {
    return { ok: false, error: 'Failed to start import: task ID was not received' };
  }

  return {
    ok: true,
    taskId: parsed.data.task_id,
    directUploadingId: parsed.data.direct_uploading_id,
  };
}

/** Разбор payload прогресса из `runTask` onProgress (status === 'STARTED'). */
export function parseProgress(data: unknown): { percent: number } | null {
  const parsed = progressSchema.safeParse(data);

  return parsed.success ? { percent: parsed.data.progress } : null;
}

/**
 * Разбор SUCCESS-результата задачи импорта — id созданных нод верхнего уровня.
 * Пустой список означает «открывать нечего»: так приходит `null` (ссылка вела на пустой файл,
 * который бэкенд пропускает, не создавая узел), так же трактуется и любая несовпавшая форма.
 */
export function parseSuccess(data: unknown): CHNode['id'][] {
  const parsed = importedNodeIdsSchema.safeParse(data);

  return parsed.success ? parsed.data : [];
}
